<template>
  <div :class="['vf-m-range-slider', customClass]">
    <!-- Main Slider -->
    <component
      v-if="vuefront$.isClient && SliderComponent"
      :is="SliderComponent"
      v-model="internalValue"
      :lazy="lazy"
      :min="min"
      :max="max"
      :step="step"
      :disabled="disabled"
      :show-ticks="showTicks"
      :tooltips="tooltips"
      :show-tooltip="showTooltip"
      @input="handleInput"
      @change="handleChange"
      class="main-range-slider"
    />
  </div>
</template>

<script lang="ts" setup>
import { inject, onMounted, ref, shallowRef, watch } from "vue";

// Define props to pass options dynamically
const props = defineProps({
  modelValue: {
    type: [Number, Array],
    default: 0,
  },
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: 100,
  },
  step: {
    type: Number,
    default: 1,
  },
  customClass: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  showTicks: {
    type: Boolean,
    default: false,
  },
  tooltips: {
    type: Boolean,
    default: true,
  },
  showTooltip: {
    type: String,
    default: "drag",
    validator: (value: any) =>
      ["always", "focus", "drag"]?.includes(value) || false,
  },
  lazy: {
    type: Boolean,
    default: true,
  },
});

// Emit events for input and change
const emit = defineEmits(["update:modelValue", "input", "change"]);
const internalValue = ref(props.modelValue);

const vuefront$ = inject<any>("$vuefront");

// Ref for dynamically importing slider
const SliderComponent = shallowRef<any>(null);

// Load the slider component dynamically when mounted
onMounted(async () => {
  try {
    const { default: Slider } = await import("@vueform/slider");
    SliderComponent.value = Slider;
  } catch (error) {
    console.error("Error loading vueform slider:", error);
  }
});

// Handle input and change events
watch(
  () => props.modelValue,
  (value) => {
    internalValue.value = value;
  },
  { deep: true }
);

const handleInput = (value: number | number[]) => {
  internalValue.value = value;
  emit("update:modelValue", value);
  emit("input", value);
};

const handleChange = (value: number | number[]) => {
  internalValue.value = value;
  emit("update:modelValue", value);
  emit("change", value);
};
</script>
<style src="@vueform/slider/themes/default.css"></style>
